import React from 'react'
import { Toaster } from 'react-hot-toast'
import { connect } from 'react-redux'
import { notifyError, notifySuccess } from './ToastMessages'
import { getSocket, initSocket } from './helpers/socket'
import {
    accessCount,
    disabledReason,
    filterLocations,
    filterPeople,
    initials,
    runSequentially,
    toggleTargets
} from './realtimeReviews/accessModel'
import { AccessRow } from './realtimeReviews/AccessRow'
import { BulkActions } from './realtimeReviews/BulkActions'
import { LoadingState } from './realtimeReviews/LoadingState'
import { addProgressEntry, hasProgress, ProviderProgress } from './realtimeReviews/ProviderProgress'
import { SaveStatus } from './realtimeReviews/SaveStatus'
import { SectionCard } from './realtimeReviews/SectionCard'
import { StatusPill } from './realtimeReviews/StatusPill'

const SAVED_VISIBLE_MS = 3000

const LOCATION_STATES = {
    pending: { tone: 'info', label: 'Provisioning' },
    failed: { tone: 'danger', label: 'Provisioning Failed' },
    dormant: { tone: 'secondary', label: 'Dormant At ShoutAboutUs' },
    none: { tone: 'secondary', label: 'Not Provisioned' }
}

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

// Each toggle request flips the current state, so a duplicated id in a bulk run would undo itself.
const uniqueIds = (ids) => [...new Set(ids)]

const personName = (person) => `${person.firstname} ${person.lastname}`

const PROGRESS_EVENT = 'ReviewProviderProgressUpdated'

const locationStates = (locations) => Object.fromEntries(
    Object.entries(locations || {}).map(([businessId, location]) => [businessId, location.state])
)

// The provider part of the page comes with it, so the selector shows before the vendor sync answers.
const initialStatus = (reviewsProvider) => {
    const status = {
        provider: 'review_trackers',
        provider_state: 'disabled',
        available: ['disabled', 'review_trackers'],
        account: false,
        businesses: {},
        business_states: {},
        sources: {},
        users: {}
    }

    if (!reviewsProvider) {
        return status
    }

    return {
        ...status,
        provider: ['shout_about_us', 'shout_about_us_pending', 'shout_about_us_failed'].includes(reviewsProvider.provider_state)
            ? 'shout_about_us'
            : 'review_trackers',
        provider_state: reviewsProvider.provider_state,
        available: reviewsProvider.available,
        shout_about_us_brand: reviewsProvider.shout_about_us_brand,
        shout_about_us_ready: reviewsProvider.shout_about_us_ready,
        carried_over: reviewsProvider.carried_over,
        business_states: locationStates(reviewsProvider.locations)
    }
}

// An expired session redirects to the login page, which answers 200 with HTML.
const readStatus = (response, label) => {
    if (response.redirected) {
        throw new Error(`${label} was redirected to ${response.url}`)
    }
    if (!response.ok) {
        throw new Error(`${label} failed with status ${response.status}`)
    }

    return response.json()
}

export class ReviewTrackers extends React.Component {
    constructor (props) {
        super(props)
        this.state = {
            syncing: true,
            loaded: false,
            changingProvider: false,
            preparing: false,
            loadError: null,
            saving: false,
            queries: { admins: '', managers: '', locations: '' },
            saveState: 'idle',
            bulkProgress: null,
            status: initialStatus(props.reviewsProvider),
            progress: {
                locations: props.reviewsProvider?.locations || {},
                entries: [],
                live: false
            }
        }

        this.pendingSaves = 0
        this.failedSinceIdle = false
        this.staleSinceIdle = false
        this.unmounted = false
        this.savedTimer = null
        this.adminsSource = null
        this.adminsCache = []

        this.handleProviderChange = this.handleProviderChange.bind(this)
        this.handlePrepare = this.handlePrepare.bind(this)
        this.handleProgress = this.handleProgress.bind(this)
        this.handleSocketConnect = this.handleSocketConnect.bind(this)
        this.handleSocketDisconnect = this.handleSocketDisconnect.bind(this)
        this.sync = this.sync.bind(this)
    }

    componentDidMount () {
        this.sync()
        this.listen()
    }

    channel () {
        return this.props.reviewsProvider?.realtime_channel || null
    }

    // Provisioning runs in queued jobs after the switch request answered; they report each step here.
    listen () {
        if (!this.channel()) {
            return
        }

        try {
            this.socket = getSocket() || initSocket()
        } catch (error) {
            console.error(error)

            return
        }

        this.socket.on('connect', this.handleSocketConnect)
        this.socket.on('disconnect', this.handleSocketDisconnect)
        this.socket.on(PROGRESS_EVENT, this.handleProgress)

        if (this.socket.connected) {
            this.handleSocketConnect()
        }
    }

    unlisten () {
        if (!this.socket) {
            return
        }

        this.socket.emit('unsubscribe', this.channel())
        this.socket.off('connect', this.handleSocketConnect)
        this.socket.off('disconnect', this.handleSocketDisconnect)
        this.socket.off(PROGRESS_EVENT, this.handleProgress)
        this.socket = null
    }

    handleSocketConnect () {
        this.socket.emit('subscribe', this.channel())
        this.setLive(true)
    }

    handleSocketDisconnect () {
        this.setLive(false)
    }

    setLive (live) {
        if (!this.unmounted) {
            this.setState(state => ({ progress: { ...state.progress, live } }))
        }
    }

    // rtf-websocket relays the Laravel envelope {event, data, socket} unchanged.
    handleProgress (payload) {
        const data = payload && typeof payload === 'object' && payload.data ? payload.data : payload

        if (this.unmounted || !data || Number(data.business_group_id) !== Number(this.props.businessGroup.id)) {
            return
        }

        const snapshot = data.snapshot || {}
        const providerChanged = snapshot.provider_state !== undefined && snapshot.provider_state !== this.state.status.provider_state

        this.setState(state => ({
            status: {
                ...state.status,
                ...(snapshot.provider_state !== undefined && { provider_state: snapshot.provider_state }),
                ...(snapshot.available !== undefined && { available: snapshot.available }),
                ...(snapshot.shout_about_us_brand !== undefined && { shout_about_us_brand: snapshot.shout_about_us_brand }),
                ...(snapshot.shout_about_us_ready !== undefined && { shout_about_us_ready: snapshot.shout_about_us_ready }),
                ...(snapshot.carried_over !== undefined && { carried_over: snapshot.carried_over }),
                ...(snapshot.locations !== undefined && { business_states: { ...state.status.business_states, ...locationStates(snapshot.locations) } })
            },
            progress: {
                ...state.progress,
                locations: snapshot.locations || state.progress.locations,
                entries: addProgressEntry(state.progress.entries, data.entry)
            }
        }))

        // The switch request reports its own refusal; a job failure has nobody else to tell.
        if (data.entry?.level === 'error' && data.entry.step !== 'switch') {
            notifyError(data.entry.message)
        }

        // Who has access depends on the provider, so the lists below are re-read when it changes.
        if (providerChanged && !this.isLocked()) {
            this.refreshStatus()
        }
    }

    sync () {
        this.setState({ syncing: true, loadError: null })

        return fetch(`${this.baseUrl()}/sync`)
            .then(response => readStatus(response, 'Sync'))
            .then(data => this.update({ status: data, loaded: true }))
            .catch(error => {
                console.error(error)
                notifyError(__('There was a problem syncing with the reviews provider'))
                this.update({ loadError: 'sync' })
            })
            .finally(() => this.update({ syncing: false }))
    }

    componentWillUnmount () {
        this.unmounted = true
        clearTimeout(this.savedTimer)
        this.unlisten()
    }

    update (state) {
        if (!this.unmounted) {
            this.setState(state)
        }
    }

    baseUrl () {
        return `/admin/business-group/${this.props.businessGroup.id}/realtime-reviews`
    }

    // The toggle endpoints flip the current state, so a second request sent before the first settles undoes it.
    isLocked () {
        return this.state.syncing || this.pendingSaves > 0
    }

    // A soft-deleted user leaves its businessGroupUser behind with a null user.
    admins () {
        const businessGroupUsers = this.props.businessGroup.businessGroupUsers

        if (businessGroupUsers !== this.adminsSource) {
            this.adminsSource = businessGroupUsers
            this.adminsCache = businessGroupUsers.filter(businessGroupUser => {
                if (!businessGroupUser.user) {
                    console.error(`Real Time Reviews: businessGroupUser ${businessGroupUser.id} has no user, leaving it out of the multisite admins`)

                    return false
                }

                return true
            }).map(businessGroupUser => businessGroupUser.user)
        }

        return this.adminsCache
    }

    isShoutAboutUs () {
        return this.state.status.provider === 'shout_about_us'
    }

    selectedProvider () {
        return ['shout_about_us_pending', 'shout_about_us_failed'].includes(this.state.status.provider_state)
            ? 'shout_about_us'
            : this.state.status.provider_state
    }

    shoutAboutUsBrand () {
        return this.state.status.shout_about_us_brand || {}
    }

    shoutAboutUsBrandState () {
        return this.shoutAboutUsBrand().state || null
    }

    readyToSwitch () {
        return Boolean(this.state.status.shout_about_us_ready?.ready)
    }

    canRetryShoutAboutUs () {
        return ['pending', 'failed'].includes(this.shoutAboutUsBrandState())
    }

    isOn (userId) {
        return this.state.status.users[`${userId}`] || false
    }

    isLocationOn (businessId) {
        return this.state.status.businesses[businessId] || false
    }

    grantable (userId) {
        return this.isShoutAboutUs() ? (this.state.status.grantable || {})[`${userId}`] : undefined
    }

    personItem (person, locationOn) {
        const reason = disabledReason({
            account: this.state.status.account,
            locationOn,
            grantable: this.grantable(person.user_id)
        })

        return { id: person.user_id, on: this.isOn(person.user_id), disabled: reason !== null, reason }
    }

    locationItem (business) {
        const reason = disabledReason({ account: this.state.status.account })

        return { id: business.business_id, on: this.isLocationOn(business.business_id), disabled: reason !== null, reason }
    }

    readMessage (response, fallback) {
        return response.json()
            .then(body => body.message || fallback)
            .catch(() => fallback)
    }

    // Never rejects: resolves to { ok, provisioning, message } so single and bulk changes share one result shape.
    sendRequest (url, options, fallback) {
        return fetch(url, options)
            .then(response => {
                if (response.redirected) {
                    console.error(`Request to ${url} was redirected to ${response.url}`)

                    return { ok: false, message: __('Your session may have expired. Reload the page and sign in again.') }
                }

                if (response.status === 202) {
                    return this.readMessage(response, __('Provisioning started'))
                        .then(message => ({ ok: true, provisioning: true, message }))
                }

                if (response.status > 299) {
                    return this.readMessage(response, fallback).then(message => ({ ok: false, message }))
                }

                return { ok: true }
            })
            .catch(error => {
                console.error(error)

                return { ok: false, message: fallback }
            })
    }

    toggleRequest (url, fallback) {
        const headers = new Headers()
        headers.append('Content-Type', 'application/json')
        headers.append('X-CSRF-TOKEN', csrfToken())

        return this.sendRequest(url, { method: 'PUT', headers }, fallback)
    }

    userToggleRequest (userId) {
        return this.toggleRequest(`${this.baseUrl()}/user/${userId}/toggle`, __('There was a problem updating the employee'))
    }

    businessToggleRequest (businessId) {
        return this.toggleRequest(`${this.baseUrl()}/business/${businessId}/toggle`, __('There was a problem updating the business'))
    }

    refreshStatus () {
        return fetch(`${this.baseUrl()}/status`)
            .then(response => readStatus(response, 'Status refresh'))
            .then(data => {
                this.update({ status: data })

                return true
            })
            .catch(error => {
                console.error(error)
                notifyError(__('There was a problem refreshing the access status'))

                return false
            })
    }

    notifyResult (result) {
        if (!result.ok) {
            notifyError(result.message)
        } else if (result.provisioning) {
            notifySuccess(result.message)
        }
    }

    beginSave () {
        this.pendingSaves++
        clearTimeout(this.savedTimer)
        this.setState({ saving: true, saveState: 'saving' })
    }

    // Overlapping saves share one indicator: it settles only when the last one finishes, and any failure wins.
    // When the status could not be re-read the switches would be stale, and flipping one would undo a saved change.
    endSave ({ saved, refreshed }) {
        this.pendingSaves--
        if (!saved) {
            this.failedSinceIdle = true
        }
        if (!refreshed) {
            this.staleSinceIdle = true
        }
        if (this.pendingSaves > 0 || this.unmounted) {
            return
        }

        let saveState = 'saved'
        if (this.failedSinceIdle) {
            saveState = 'error'
        } else if (this.staleSinceIdle) {
            saveState = 'idle'
        }
        const loadError = this.staleSinceIdle ? 'refresh' : this.state.loadError
        this.failedSinceIdle = false
        this.staleSinceIdle = false
        this.setState({ saving: false, saveState, bulkProgress: null, loadError })

        if (saveState === 'saved') {
            this.savedTimer = setTimeout(() => this.update({ saveState: 'idle' }), SAVED_VISIBLE_MS)
        }
    }

    reportUnexpected (error) {
        console.error(error)
        notifyError(__('There was a problem saving your changes'))
    }

    async runChange (request) {
        if (this.isLocked()) {
            return
        }

        this.beginSave()
        let outcome = { saved: false, refreshed: false }
        try {
            const result = await request()
            this.notifyResult(result)
            outcome = { saved: result.ok, refreshed: await this.refreshStatus() }
        } catch (error) {
            this.reportUnexpected(error)
        } finally {
            this.endSave(outcome)
        }
    }

    handleUserToggle (userId) {
        return this.runChange(() => this.userToggleRequest(userId))
    }

    handleBusinessToggle (businessId) {
        return this.runChange(() => this.businessToggleRequest(businessId))
    }

    async handlePrepare () {
        const headers = new Headers()
        headers.append('Content-Type', 'application/json')
        headers.append('Accept', 'application/json')
        headers.append('X-CSRF-TOKEN', csrfToken())

        if (this.isLocked()) {
            return
        }

        this.setState({ preparing: true })
        try {
            await this.runChange(() => this.sendRequest(
                `${this.baseUrl()}/shout-about-us/prepare`,
                { method: 'POST', headers },
                __('There was a problem preparing ShoutAboutUs')
            ))
        } finally {
            this.update({ preparing: false })
        }
    }

    async handleProviderChange (event) {
        const provider = event.target.value
        const headers = new Headers()
        headers.append('Content-Type', 'application/json')
        headers.append('Accept', 'application/json')
        headers.append('X-CSRF-TOKEN', csrfToken())

        if (this.isLocked()) {
            return
        }

        this.setState({ changingProvider: true })
        try {
            await this.runChange(() => this.sendRequest(
                `${this.baseUrl()}/provider`,
                { method: 'POST', headers, body: JSON.stringify({ provider }) },
                __('There was a problem changing the reviews provider')
            ))
        } finally {
            this.update({ changingProvider: false })
        }
    }

    async runBulk (ids, request) {
        if (ids.length === 0 || this.isLocked()) {
            return
        }

        this.beginSave()
        this.setState({ bulkProgress: { current: 1, total: ids.length } })

        let outcome = { saved: false, refreshed: false }
        try {
            let provisioning = 0
            const { failed } = await runSequentially(
                ids,
                async (id) => {
                    const result = await request(id)
                    if (result.provisioning) {
                        provisioning++
                    }

                    return result
                },
                (processed, total) => this.update({ bulkProgress: { current: Math.min(processed + 1, total), total } })
            )

            if (failed.length > 0) {
                notifyError(__(':failed of :total changes could not be saved', { ':failed': failed.length, ':total': ids.length }))
            }
            if (provisioning > 0) {
                notifySuccess(__('Provisioning started'))
            }

            outcome = { saved: failed.length === 0, refreshed: await this.refreshStatus() }
        } catch (error) {
            this.reportUnexpected(error)
        } finally {
            this.endSave(outcome)
        }
    }

    bulkUsers (items, turnOn) {
        return this.runBulk(uniqueIds(toggleTargets(items, turnOn)), id => this.userToggleRequest(id))
    }

    bulkBusinesses (items, turnOn) {
        return this.runBulk(uniqueIds(toggleTargets(items, turnOn)), id => this.businessToggleRequest(id))
    }

    handleQueryChange (card, value) {
        this.setState(state => ({ queries: { ...state.queries, [card]: value } }))
    }

    searchBox (card, label) {
        return (
            <div className="rt-search input-group feedback-form-control mb-0">
                <div className="input-group-prepend">
                    <span className="input-group-text"><i className="fas fa-search" aria-hidden="true" /></span>
                </div>
                <input
                    type="search"
                    className="form-control pl-0"
                    placeholder={label}
                    aria-label={label}
                    value={this.state.queries[card]}
                    onChange={event => this.handleQueryChange(card, event.target.value)}
                />
            </div>
        )
    }

    locked () {
        return this.state.syncing || this.state.saving
    }

    bulkActions (items, labels, onBulk) {
        const busy = this.locked()

        return (
            <BulkActions
                onLabel={labels.on}
                offLabel={labels.off}
                canTurnOn={!busy && toggleTargets(items, true).length > 0}
                canTurnOff={!busy && toggleTargets(items, false).length > 0}
                onTurnOn={() => onBulk(items, true)}
                onTurnOff={() => onBulk(items, false)}
            />
        )
    }

    countLabel (ids, statusMap) {
        const { on, total } = accessCount(uniqueIds(ids), statusMap)

        return __(':on of :total have access', { ':on': on, ':total': total })
    }

    emptyText (card, hasAny, emptyMessage) {
        const text = hasAny
            ? __('No matches for ":query"', { ':query': this.state.queries[card].trim() })
            : emptyMessage

        return <p className="rt-empty">{text}</p>
    }

    accessCard ({ count, toolbar, actions, ...props }, renderContent) {
        if (!this.state.loaded) {
            return (
                <SectionCard {...props} collapsible busy>
                    <LoadingState text={__('Loading access settings...')} />
                </SectionCard>
            )
        }

        return (
            <SectionCard {...props} collapsible count={count} toolbar={toolbar} actions={actions}>
                {renderContent()}
            </SectionCard>
        )
    }

    renderPersonRow (person, item) {
        return (
            <AccessRow
                key={person.user_id}
                avatar={initials(person.firstname, person.lastname)}
                name={personName(person)}
                detail={person.title}
                checked={item.on}
                reason={item.reason}
                locked={this.locked()}
                onChange={() => this.handleUserToggle(person.user_id)}
            />
        )
    }

    renderProviderCard () {
        const { status, syncing, loaded, changingProvider, progress } = this.state

        // The page renders the provider state with it, so only a page without it waits for the first sync.
        if (!loaded && !this.props.reviewsProvider) {
            return (
                <SectionCard
                    id="rt-provider"
                    busy
                    title={__('Reviews Provider')}
                    description={__('Choose the service that provides reviews for this business group.')}
                >
                    <LoadingState text={__('Loading reviews provider...')} />
                </SectionCard>
            )
        }

        const locked = this.locked()
        const brandState = this.shoutAboutUsBrandState()
        const brandReason = this.shoutAboutUsBrand().reason
        const shoutAboutUsAvailable = status.available.includes('shout_about_us')
        const onReviewTrackers = status.provider_state === 'review_trackers'
        // A ReviewTrackers group is prepared first, from the setup below; the selector only switches.
        const shoutAboutUsSelectable = shoutAboutUsAvailable && (!onReviewTrackers || this.readyToSwitch())
        const shoutAboutUsPrimaryRetry = ['shout_about_us_pending', 'shout_about_us_failed'].includes(status.provider_state)

        return (
            <SectionCard
                id="rt-provider"
                title={__('Reviews Provider')}
                description={__('Choose the service that provides reviews for this business group.')}
            >
                <div className="rt-provider">
                    <div className="rt-provider-select input-group feedback-form-control border mb-0">
                        <select
                            className="form-control input"
                            aria-label={__('Reviews Provider')}
                            disabled={locked}
                            value={this.selectedProvider()}
                            onChange={this.handleProviderChange}
                        >
                            <option value="disabled">{__('Disabled')}</option>
                            <option value="shout_about_us" disabled={!shoutAboutUsSelectable}>
                                {shoutAboutUsAvailable && !shoutAboutUsSelectable
                                    ? __('ShoutAboutUs (set it up below first)')
                                    : __('ShoutAboutUs')}
                            </option>
                            <option value="review_trackers">{__('ReviewTrackers')}</option>
                        </select>
                    </div>
                    {changingProvider && <LoadingState text={__('Changing reviews provider...')} />}
                    {shoutAboutUsPrimaryRetry && brandState === 'pending' &&
                        <StatusPill tone="secondary">{__('Being Prepared')}</StatusPill>
                    }
                    {shoutAboutUsPrimaryRetry && brandState === 'failed' &&
                        <StatusPill tone="danger">
                            {brandReason ? __('Preparation Failed: :reason', { ':reason': brandReason }) : __('Preparation Failed')}
                        </StatusPill>
                    }
                    {shoutAboutUsPrimaryRetry &&
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={locked}
                            onClick={() => this.handleProviderChange({ target: { value: 'shout_about_us' } })}
                        >{__('Retry')}</button>
                    }
                </div>
                {!shoutAboutUsAvailable &&
                    <p className="rt-provider-note">{__('ShoutAboutUs is not enabled for this tenant')}</p>
                }
                {shoutAboutUsAvailable && onReviewTrackers && this.renderShoutAboutUsSetup()}
                {shoutAboutUsAvailable && !onReviewTrackers && hasProgress({ brand: status.shout_about_us_brand, locations: progress.locations, entries: progress.entries }) &&
                    <ProviderProgress
                        brand={status.shout_about_us_brand}
                        locations={progress.locations}
                        entries={progress.entries}
                        businesses={this.props.businessGroup.businesses}
                        live={progress.live}
                        providerState={status.provider_state}
                        readiness={status.shout_about_us_ready}
                        carriedOver={status.carried_over}
                    />
                }
                {!syncing && !status.account &&
                    <div className="rt-info-note">
                        <i className="fas fa-info-circle" aria-hidden="true" />
                        <span>{__('Turn on a reviews provider to manage access below.')}</span>
                    </div>
                }
            </SectionCard>
        )
    }

    renderShoutAboutUsSetup () {
        const { status, progress, preparing } = this.state
        const locked = this.locked()
        const brandState = this.shoutAboutUsBrandState()
        const readiness = status.shout_about_us_ready || { ready: false, waiting: [], failed: [] }
        const failedCount = (readiness.failed || []).length
        const canRetry = brandState === 'failed' || failedCount > 0

        const prepareButton = (
            <div className="rt-setup-actions">
                <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={locked}
                    onClick={this.handlePrepare}
                >{__('Prepare ShoutAboutUs')}</button>
                {preparing && <LoadingState text={__('Starting the preparation...')} />}
            </div>
        )

        if (brandState === null && progress.entries.length === 0) {
            return (
                <div className="rt-setup">
                    <h3 className="rt-progress-title">{__('ShoutAboutUs Setup')}</h3>
                    <p className="rt-provider-note">
                        {__('Prepare ShoutAboutUs first: the business group and its locations are registered there while the group stays on ReviewTrackers. Switch once everything is ready.')}
                    </p>
                    {prepareButton}
                </div>
            )
        }

        if (brandState === null) {
            return (
                <ProviderProgress
                    title={__('ShoutAboutUs Setup')}
                    brand={status.shout_about_us_brand}
                    locations={progress.locations}
                    entries={progress.entries}
                    businesses={this.props.businessGroup.businesses}
                    live={progress.live}
                    providerState={status.provider_state}
                    readiness={readiness}
                    carriedOver={status.carried_over}
                >
                    {prepareButton}
                </ProviderProgress>
            )
        }

        return (
            <ProviderProgress
                title={__('ShoutAboutUs Setup')}
                brand={status.shout_about_us_brand}
                locations={progress.locations}
                entries={progress.entries}
                businesses={this.props.businessGroup.businesses}
                live={progress.live}
                providerState={status.provider_state}
                readiness={readiness}
                carriedOver={status.carried_over}
            >
                {readiness.ready && failedCount > 0 &&
                    <p className="rt-setup-warning">
                        {__(':count locations failed and will not be on ShoutAboutUs. Retry them or switch without them.', { ':count': failedCount })}
                    </p>
                }
                <div className="rt-setup-actions">
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={locked || !readiness.ready}
                        onClick={() => this.handleProviderChange({ target: { value: 'shout_about_us' } })}
                    >{__('Switch To ShoutAboutUs')}</button>
                    {canRetry &&
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={locked}
                            onClick={this.handlePrepare}
                        >{brandState === 'failed' ? __('Retry') : __('Retry Failed Locations')}</button>
                    }
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={locked}
                        onClick={() => this.handleProviderChange({ target: { value: 'review_trackers' } })}
                    >{__('Cancel Setup')}</button>
                    {preparing && <LoadingState text={__('Starting the preparation...')} />}
                </div>
            </ProviderProgress>
        )
    }

    renderAdminsCard () {
        const admins = this.admins()
        const visible = filterPeople(admins, this.state.queries.admins)
        const items = visible.map(person => this.personItem(person))

        return this.accessCard({
            id: 'rt-admins',
            title: __('Multisite Admins'),
            description: __('Choose which multisite admins have access.'),
            count: this.countLabel(admins.map(admin => admin.user_id), this.state.status.users),
            toolbar: admins.length > 0 && this.searchBox('admins', __('Search Multisite Admins')),
            actions: admins.length > 0 && this.bulkActions(items, { on: __('Turn All On'), off: __('Turn All Off') }, (targets, turnOn) => this.bulkUsers(targets, turnOn))
        }, () => (
            visible.length === 0
                ? this.emptyText('admins', admins.length > 0, __('No multisite admins.'))
                : <div className="rt-rows">{visible.map((person, index) => this.renderPersonRow(person, items[index]))}</div>
        ))
    }

    renderRegionalManagersCard () {
        const regions = this.props.businessGroup.regions || []
        const allManagers = regions.flatMap(region => region.users || [])
        const visibleRegions = regions
            .map(region => ({ region, users: filterPeople(region.users || [], this.state.queries.managers) }))
            .filter(group => group.users.length > 0)
        const items = visibleRegions.flatMap(group => group.users.map(person => this.personItem(person)))

        return this.accessCard({
            id: 'rt-regional-managers',
            title: __('Regional Managers'),
            description: __('Choose which regional managers have access.'),
            count: this.countLabel(allManagers.map(manager => manager.user_id), this.state.status.users),
            toolbar: allManagers.length > 0 && this.searchBox('managers', __('Search Regional Managers')),
            actions: allManagers.length > 0 && this.bulkActions(items, { on: __('Turn All On'), off: __('Turn All Off') }, (targets, turnOn) => this.bulkUsers(targets, turnOn))
        }, () => (
            visibleRegions.length === 0
                ? this.emptyText('managers', allManagers.length > 0, __('No regional managers.'))
                : visibleRegions.map(({ region, users }) => (
                    <div className="rt-group" key={region.id}>
                        <div className="rt-group-title">{region.name}</div>
                        <div className="rt-rows">
                            {users.map(person => this.renderPersonRow(person, this.personItem(person)))}
                        </div>
                    </div>
                ))
        ))
    }

    renderLocation ({ business, employees }) {
        const location = this.locationItem(business)
        const locationState = this.isShoutAboutUs() ? LOCATION_STATES[this.state.status.business_states[business.business_id]] : null
        const employeeItems = employees.map(person => this.personItem(person, location.on))

        return (
            <div className="rt-location" key={business.business_id} data-testid={`location-${business.business_id}`}>
                <AccessRow
                    avatar={<i className="fas fa-store" />}
                    name={business.name}
                    detail={business.address}
                    pill={locationState && <StatusPill tone={locationState.tone}>{__(locationState.label)}</StatusPill>}
                    checked={location.on}
                    reason={location.reason}
                    locked={this.locked()}
                    onChange={() => this.handleBusinessToggle(business.business_id)}
                />
                {this.isShoutAboutUs() && <div className="rt-sources">{this.renderSources(business.business_id)}</div>}
                <div className="rt-employees">
                    <div className="rt-employees-header">
                        <span className="rt-employees-title">{__('Employees')}</span>
                        {employees.length > 0 &&
                            this.bulkActions(employeeItems, { on: __('All Employees On'), off: __('All Employees Off') }, (targets, turnOn) => this.bulkUsers(targets, turnOn))
                        }
                    </div>
                    {employees.length === 0
                        ? <p className="rt-empty">{__('No employees.')}</p>
                        : <div className="rt-rows">{employees.map((person, index) => this.renderPersonRow(person, employeeItems[index]))}</div>
                    }
                </div>
            </div>
        )
    }

    renderLocationsCard () {
        const businesses = this.props.businessGroup.businesses
        const visible = filterLocations(businesses, this.state.queries.locations)
        const items = visible.map(({ business }) => this.locationItem(business))

        return this.accessCard({
            id: 'rt-locations',
            title: __('Locations'),
            description: __('Turn on a location, then choose which of its employees have access.'),
            count: this.countLabel(businesses.map(business => business.business_id), this.state.status.businesses),
            toolbar: businesses.length > 0 && this.searchBox('locations', __('Search Locations or Employees')),
            actions: businesses.length > 0 && this.bulkActions(items, { on: __('Turn All On'), off: __('Turn All Off') }, (targets, turnOn) => this.bulkBusinesses(targets, turnOn))
        }, () => (
            visible.length === 0
                ? this.emptyText('locations', businesses.length > 0, __('No locations.'))
                : <div className="rt-locations">{visible.map(entry => this.renderLocation(entry))}</div>
        ))
    }

    renderLoadError () {
        const afterChange = this.state.loadError === 'refresh'

        return (
            <section className="card rt-card rt-load-error" role="alert" aria-labelledby="rt-load-error-title">
                <div className="card-body">
                    <h2 id="rt-load-error-title" className="rt-card-title">
                        {afterChange ? __('Could Not Refresh Access Settings') : __('Could Not Load Access Settings')}
                    </h2>
                    <p className="rt-card-description">
                        {afterChange
                            ? __('Your changes may have been saved, but the page could not load the current settings. Try again to see them.')
                            : __('We could not reach the reviews provider, so the current access settings are unknown.')}
                    </p>
                    <button type="button" className="btn btn-outline-primary" onClick={this.sync}>{__('Try Again')}</button>
                </div>
            </section>
        )
    }

    renderAccessSettings () {
        return (
            <>
                {this.renderProviderCard()}
                {this.renderAdminsCard()}
                {this.renderRegionalManagersCard()}
                {this.renderLocationsCard()}
            </>
        )
    }

    renderSources (businessId) {
        const sources = (this.state.status.sources || {})[businessId] || []

        if (sources.length === 0) {
            return this.state.status.business_states[businessId] === 'enabled' &&
                <StatusPill tone="secondary">{__('No collection state received from ShoutAboutUs yet')}</StatusPill>
        }

        return sources.map(source => {
            const pull = source.pull || {}
            const post = source.post || {}
            const label = {
                connected: __('collecting'),
                disconnected: __('not collecting')
            }[pull.status] || __('waiting for ShoutAboutUs')
            const tone = { connected: 'success', disconnected: 'danger' }[pull.status] || 'secondary'
            const detail = [pull.reason, pull.error_code].filter(Boolean).join(' - ')

            return (
                <StatusPill key={source.external_site_id} tone={tone}>
                    {`${source.name}: ${label}` + (detail ? ` (${detail})` : '') + (post.status === 'connected' ? `, ${__('replies on')}` : '')}
                </StatusPill>
            )
        })
    }

    render () {
        return (
            <div className="w-100 right-side-content">
                <div className="main-section">
                    <div className="col-12 mt-3">
                        <div className="rt-reviews-settings">
                            <header className="rt-page-header">
                                <a
                                    href={`/admin/business-group/${this.props.businessGroup.id}/edit`}
                                    className="btn btn-sm btn-outline-secondary rt-back"
                                >
                                    <i className="fas fa-arrow-left mr-1" aria-hidden="true"></i> {__('Back')}
                                </a>
                                <div className="rt-page-meta">
                                    <SaveStatus
                                        syncing={this.state.syncing}
                                        progress={this.state.bulkProgress}
                                        saveState={this.state.saveState}
                                    />
                                </div>
                            </header>

                            {this.state.loadError ? this.renderLoadError() : this.renderAccessSettings()}
                        </div>

                        <Toaster
                            position="top-center"
                            toastOptions={{
                                duration: 5000
                            }}
                        />
                    </div>
                </div>
            </div>
        )
    }
}

function mapStateToProps (state) {
    return {
        businessGroup: state.businessGroup,
        title: state.title,
        realTimeReviewsLabel: state.realTimeReviewsLabel,
        reviewsProvider: state.reviewsProvider
    }
}

export default connect(mapStateToProps)(ReviewTrackers)
